import React, { useState, useEffect } from 'react';
import { Settings, RefreshCw, Database, Users, Building, Trash2, CheckCircle, XCircle, Activity, AlertTriangle, Play, Wrench } from 'lucide-react';
// Using Firebase as backend
import { enterpriseSync } from '../utils/sync/enterpriseSync';
import { syncQueueManager } from '../utils/offline/syncQueue';

interface AdminDebugPanelProps {
  showPanel?: boolean;
  onClose?: () => void;
}

export function AdminDebugPanel({ showPanel = false, onClose }: AdminDebugPanelProps = {}) {
  const [users, setUsers] = useState<any[]>([]);
  const [wells, setWells] = useState<any[]>([]);
  const [companies, setCompanies] = useState<any[]>([]);
  const [syncResult, setSyncResult] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  
  // Queue State
  const [queueStatus, setQueueStatus] = useState<any>(null);
  const [queueItems, setQueueItems] = useState<any[]>([]);
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  useEffect(() => {
    loadData();
    loadQueueData();
    
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const loadData = () => {
    // Load users from localStorage (authService stores them there)
    const usersData = localStorage.getItem('users');
    setUsers(usersData ? JSON.parse(usersData) : []);
    
    const wellsData = localStorage.getItem('wells');
    setWells(wellsData ? JSON.parse(wellsData) : []);
    
    const companiesData = localStorage.getItem('companies');
    setCompanies(companiesData ? JSON.parse(companiesData) : []);
  };

  const loadQueueData = async () => {
    try {
      const status = await syncQueueManager.getQueueStatus();
      setQueueStatus(status);
      
      // Get all items (we need to access the private method or use the public one if available)
      // Since getPendingItems only returns pending, we might need to expose getAllItems in syncQueueManager
      // For now, let's try to get pending items at least
      const pendingItems = await syncQueueManager.getPendingItems();
      setQueueItems(pendingItems);
    } catch (error) {
      console.error('Failed to load queue data:', error);
    }
  };

  const handleRetryQueue = async () => {
    setLoading(true);
    try {
      await syncQueueManager.retryFailedItems();
      await loadQueueData();
      alert('Retry initiated');
    } catch (error) {
      alert('Retry failed');
    } finally {
      setLoading(false);
    }
  };

  const handleClearQueue = async () => {
    if (confirm('Are you sure you want to clear the sync queue? Pending changes will be lost.')) {
      setLoading(true);
      try {
        await syncQueueManager.clearQueue();
        await loadQueueData();
        alert('Queue cleared');
      } catch (error) {
        alert('Failed to clear queue');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleSync = async () => {
    setLoading(true);
    try {
      // Trigger enterprise sync
      await enterpriseSync.syncNow();
      setSyncResult({ 
        success: true, 
        message: 'Sync completed',
        synced: { users: 0, wells: 0, companies: 0 },
        errors: []
      });
      loadData();
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      setSyncResult({ 
        success: false, 
        message: errorMessage,
        synced: { users: 0, wells: 0, companies: 0 },
        errors: [errorMessage]
      });
    }
    setLoading(false);
  };

  const handleClearCache = () => {
    if (confirm('Clear all cached data? This will force a fresh sync.')) {
      // Clear relevant localStorage items
      localStorage.removeItem('wells');
      localStorage.removeItem('users');
      localStorage.removeItem('companies');
      loadData();
      setSyncResult(null);
    }
  };

  const handleTestBackend = async () => {
    setLoading(true);
    try {
      // Get current tenant ID for testing
      const { getCompanyId } = await import('../utils/tenantContext');
      const companyId = getCompanyId();
      
      // Test Firebase connectivity by fetching wells
      const wells = await import('../utils/firebase/api').then(m => m.default.fetchWells(companyId));
      alert(`Backend Health:\n\nFirebase Connected!\nWells found: ${wells?.length || 0}`);
    } catch (error) {
      alert(`Backend Error: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  // Migration handler for assignment schema
  const handleMigrateAssignmentSchema = async () => {
    if (!confirm('Run Assignment Schema Migration?\n\nThis will:\n1. Copy users to company collection\n2. Add assignment fields to wells\n\nThis is safe to run multiple times.')) {
      return;
    }
    
    setLoading(true);
    try {
      const { runAssignmentSchemaMigration } = await import('../utils/migrations/assignmentSchemaMigration');
      const result = await runAssignmentSchemaMigration();
      
      if (result.success) {
        alert(`Migration Successful!\n\nUsers copied: ${result.usersCopied}\nWells updated: ${result.wellsUpdated}`);
      } else {
        alert(`Migration completed with errors:\n\nUsers copied: ${result.usersCopied}\nWells updated: ${result.wellsUpdated}\nErrors: ${result.errors.join(', ')}`);
      }
    } catch (error) {
      alert(`Migration Error: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  if (!showPanel) {
    return null;
  }

  // If no onClose provided, render as embedded component (not modal)
  const isModal = !!onClose;

  if (!isModal) {
    return (
      <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gray-800 text-white">
          <div className="flex items-center gap-3">
            <Database className="w-6 h-6" />
            <div>
              <h2 className="text-xl font-bold">Admin Debug Panel</h2>
              <p className="text-sm text-gray-300">Backend Sync Status & Data</p>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="p-4 bg-gray-50 border-b border-gray-200">
          <div className="flex flex-wrap gap-3">
            <button
              onClick={handleSync}
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 transition-colors"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              Force Sync
            </button>
            
            <button
              onClick={handleClearCache}
              className="flex items-center gap-2 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors"
            >
              <Trash2 className="w-4 h-4" />
              Clear Cache
            </button>
            
            <button
              onClick={handleTestBackend}
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-300 transition-colors"
            >
              <Database className="w-4 h-4" />
              Test Backend
            </button>
          </div>
          
          {/* Sync Result */}
          {syncResult && (
            <div className={`mt-4 p-4 rounded-lg ${syncResult.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
              <div className="flex items-start gap-2">
                {syncResult.success ? (
                  <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
                ) : (
                  <XCircle className="w-5 h-5 text-red-600 mt-0.5" />
                )}
                <div className="flex-1">
                  <p className={`font-medium ${syncResult.success ? 'text-green-900' : 'text-red-900'}`}>
                    {syncResult.message}
                  </p>
                  {syncResult.synced && (
                    <div className="mt-2 grid grid-cols-3 gap-4 text-sm">
                      <div>
                        <span className="text-gray-600">Users:</span>{' '}
                        <span className="font-semibold">{syncResult.synced.users}</span>
                      </div>
                      <div>
                        <span className="text-gray-600">Wells:</span>{' '}
                        <span className="font-semibold">{syncResult.synced.wells}</span>
                      </div>
                      <div>
                        <span className="text-gray-600">Companies:</span>{' '}
                        <span className="font-semibold">{syncResult.synced.companies}</span>
                      </div>
                    </div>
                  )}
                  {syncResult.errors && syncResult.errors.length > 0 && (
                    <div className="mt-2">
                      <p className="text-sm font-medium text-red-800">Errors:</p>
                      <ul className="mt-1 list-disc list-inside text-sm text-red-700">
                        {syncResult.errors.map((error: string, idx: number) => (
                          <li key={idx}>{error}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Sync Queue Manager */}
        <div className="p-6 border-b border-gray-200 bg-gray-50">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Activity className="w-5 h-5 text-blue-600" />
              <h3 className="text-lg font-bold text-gray-900">Sync Queue Manager</h3>
            </div>
            <div className={`px-3 py-1 rounded-full text-sm font-medium flex items-center gap-2 ${isOnline ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
              <div className={`w-2 h-2 rounded-full ${isOnline ? 'bg-green-600' : 'bg-red-600'}`}></div>
              {isOnline ? 'Online' : 'Offline'}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
            <div className="bg-white p-3 rounded-lg border border-gray-200">
              <div className="text-sm text-gray-500">Pending</div>
              <div className="text-2xl font-bold text-orange-600">{queueStatus?.pending || 0}</div>
            </div>
            <div className="bg-white p-3 rounded-lg border border-gray-200">
              <div className="text-sm text-gray-500">Syncing</div>
              <div className="text-2xl font-bold text-blue-600">{queueStatus?.syncing || 0}</div>
            </div>
            <div className="bg-white p-3 rounded-lg border border-gray-200">
              <div className="text-sm text-gray-500">Failed</div>
              <div className="text-2xl font-bold text-red-600">{queueStatus?.failed || 0}</div>
            </div>
            <div className="bg-white p-3 rounded-lg border border-gray-200">
              <div className="text-sm text-gray-500">Total</div>
              <div className="text-2xl font-bold text-gray-900">{queueStatus?.total || 0}</div>
            </div>
          </div>

          <div className="flex gap-3 mb-4">
            <button
              onClick={handleRetryQueue}
              disabled={loading || !queueStatus?.failed}
              className="flex items-center gap-2 px-3 py-1.5 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-sm"
            >
              <Play className="w-4 h-4" />
              Retry Failed
            </button>
            <button
              onClick={handleClearQueue}
              disabled={loading || !queueStatus?.total}
              className="flex items-center gap-2 px-3 py-1.5 bg-red-600 text-white rounded hover:bg-red-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-sm"
            >
              <Trash2 className="w-4 h-4" />
              Clear Queue
            </button>
            <button
              onClick={loadQueueData}
              className="flex items-center gap-2 px-3 py-1.5 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 text-sm"
            >
              <RefreshCw className="w-4 h-4" />
              Refresh
            </button>
          </div>

          {queueItems.length > 0 && (
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden max-h-96 overflow-y-auto">
              {/* Desktop Table */}
              <table className="min-w-full divide-y divide-gray-200 hidden md:table">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Entity</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Retries</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Error</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {queueItems.map((item) => (
                    <tr key={item.id}>
                      <td className="px-4 py-2 text-sm text-gray-900">{item.type}</td>
                      <td className="px-4 py-2 text-sm text-gray-500">{item.entity}</td>
                      <td className="px-4 py-2 text-sm">
                        <span className={`px-2 py-0.5 rounded-full text-xs ${
                          item.status === 'failed' ? 'bg-red-100 text-red-800' :
                          item.status === 'syncing' ? 'bg-blue-100 text-blue-800' :
                          'bg-yellow-100 text-yellow-800'
                        }`}>
                          {item.status}
                        </span>
                      </td>
                      <td className="px-4 py-2 text-sm text-gray-500">{item.retryCount}</td>
                      <td className="px-4 py-2 text-sm text-red-600 truncate max-w-xs" title={item.lastError}>
                        {item.lastError || '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Mobile List View */}
              <div className="md:hidden divide-y divide-gray-200">
                {queueItems.map((item) => (
                  <div key={item.id} className="p-3 space-y-2">
                    <div className="flex justify-between items-start">
                      <div>
                        <div className="font-medium text-sm text-gray-900">{item.type} {item.entity}</div>
                        <div className="text-xs text-gray-500">Retries: {item.retryCount}</div>
                      </div>
                      <span className={`px-2 py-0.5 rounded-full text-xs ${
                        item.status === 'failed' ? 'bg-red-100 text-red-800' :
                        item.status === 'syncing' ? 'bg-blue-100 text-blue-800' :
                        'bg-yellow-100 text-yellow-800'
                      }`}>
                        {item.status}
                      </span>
                    </div>
                    {item.lastError && (
                      <div className="text-xs text-red-600 bg-red-50 p-1 rounded">
                        {item.lastError}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Data Cards */}
        <div className="p-6 space-y-6 overflow-auto max-h-[500px]">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Users Card */}
            <div className="border border-gray-200 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-3">
                <Users className="w-5 h-5 text-blue-600" />
                <h3 className="font-semibold">Users</h3>
              </div>
              <p className="text-3xl font-bold text-blue-600">{users.length}</p>
              <p className="text-sm text-gray-500 mt-1">Total users cached</p>
            </div>

            {/* Wells Card */}
            <div className="border border-gray-200 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-3">
                <Database className="w-5 h-5 text-green-600" />
                <h3 className="font-semibold">Wells</h3>
              </div>
              <p className="text-3xl font-bold text-green-600">{wells.length}</p>
              <p className="text-sm text-gray-500 mt-1">Total wells cached</p>
            </div>

            {/* Companies Card */}
            <div className="border border-gray-200 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-3">
                <Building className="w-5 h-5 text-purple-600" />
                <h3 className="font-semibold">Companies</h3>
              </div>
              <p className="text-3xl font-bold text-purple-600">{companies.length}</p>
              <p className="text-sm text-gray-500 mt-1">Total companies cached</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Modal version (when onClose is provided)
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-end sm:items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gray-800 text-white">
          <div className="flex items-center gap-3">
            <Database className="w-6 h-6" />
            <div>
              <h2 className="text-xl font-bold">Admin Debug Panel</h2>
              <p className="text-sm text-gray-300">Backend Sync Status & Data</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-300 hover:text-white"
          >
            <XCircle className="w-6 h-6" />
          </button>
        </div>

        {/* Quick Actions */}
        <div className="p-4 bg-gray-50 border-b border-gray-200">
          <div className="flex flex-wrap gap-3">
            <button
              onClick={handleSync}
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 transition-colors"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              Force Sync
            </button>
            
            <button
              onClick={handleClearCache}
              className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
            >
              <Trash2 className="w-4 h-4" />
              Clear Cache
            </button>
            
            <button
              onClick={handleTestBackend}
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-300 transition-colors"
            >
              <Database className="w-4 h-4" />
              Test Backend
            </button>
          </div>

          {/* Last Sync Time */}
          <div className="mt-3 text-sm text-gray-600">
            Last Sync: {localStorage.getItem('lastSyncTime') || 'Never'}
          </div>

          {/* Sync Result */}
          {syncResult && (
            <div className={`mt-3 p-3 rounded-lg ${syncResult.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
              <div className="flex items-center gap-2 mb-2">
                {syncResult.success ? (
                  <CheckCircle className="w-5 h-5 text-green-600" />
                ) : (
                  <XCircle className="w-5 h-5 text-red-600" />
                )}
                <span className={`font-medium ${syncResult.success ? 'text-green-700' : 'text-red-700'}`}>
                  Sync {syncResult.success ? 'Successful' : 'Failed'}
                </span>
              </div>
              <div className="text-sm text-gray-700">
                <div>Users: {syncResult.synced.users}</div>
                <div>Wells: {syncResult.synced.wells}</div>
                <div>Companies: {syncResult.synced.companies}</div>
                {syncResult.errors.length > 0 && (
                  <div className="mt-2 text-red-600">
                    Errors: {syncResult.errors.join(', ')}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Data Display */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="space-y-6">
            {/* Users */}
            <div>
              <div className="flex items-center gap-2 mb-3">
                <Users className="w-5 h-5 text-blue-600" />
                <h3 className="text-lg font-bold text-gray-900">
                  Users ({users.length})
                </h3>
              </div>
              {users.length === 0 ? (
                <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <p className="text-yellow-700">
                    ⚠️ No users found! Backend may not be initialized. Click "Force Sync" to load users.
                  </p>
                </div>
              ) : (
                <div className="space-y-2">
                  {users.map((user) => (
                    <div
                      key={user.id}
                      className="p-3 bg-white border border-gray-200 rounded-lg"
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="font-medium text-gray-900">
                            {user.name || user.email || user.id}
                          </div>
                          <div className="text-sm text-gray-500">
                            {user.email} • {user.role}
                            {user.isSuperAdmin && (
                              <span className="ml-2 px-2 py-0.5 bg-purple-100 text-purple-700 text-xs rounded-full">
                                SuperAdmin
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="text-xs text-gray-400">
                          ID: {user.id}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Wells */}
            <div>
              <div className="flex items-center gap-2 mb-3">
                <Database className="w-5 h-5 text-green-600" />
                <h3 className="text-lg font-bold text-gray-900">
                  Wells ({wells.length})
                </h3>
              </div>
              {wells.length === 0 ? (
                <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
                  <p className="text-gray-600">No wells found</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {wells.slice(0, 10).map((well) => (
                    <div
                      key={well.id}
                      className="p-3 bg-white border border-gray-200 rounded-lg"
                    >
                      <div className="font-medium text-gray-900">{well.name}</div>
                      <div className="text-sm text-gray-500">
                        {well.location} • {well.status}
                      </div>
                    </div>
                  ))}
                  {wells.length > 10 && (
                    <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg text-center text-gray-500">
                      +{wells.length - 10} more wells
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Companies */}
            <div>
              <div className="flex items-center gap-2 mb-3">
                <Building className="w-5 h-5 text-purple-600" />
                <h3 className="text-lg font-bold text-gray-900">
                  Companies ({companies.length})
                </h3>
              </div>
              {companies.length === 0 ? (
                <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
                  <p className="text-gray-600">No companies found (this is normal if you haven't created any multi-tenant companies yet)</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {companies.map((company) => (
                    <div
                      key={company.id}
                      className="p-3 bg-white border border-gray-200 rounded-lg"
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="font-medium text-gray-900">
                            {company.companyName}
                          </div>
                          <div className="text-sm text-gray-500">
                            {company.subdomain} • {company.status}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Role Structure */}
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <h4 className="font-bold text-blue-900 mb-2">Role Structure</h4>
              <div className="space-y-2 text-sm text-blue-800">
                <div className="pt-2">
                  <p className="text-xs text-blue-700">
                    <strong>Role Structure:</strong><br />
                    • <strong>superadmin</strong> = System administrators (OilWellX team)<br />
                    • <strong>manager</strong> = Company owners (manage company & users)<br />
                    • <strong>user</strong> = Pumpers/field workers (view wells, enter data)
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}